import { Content } from './types';

/**
 * remove a key from content
 * a key can have one or more children
 * so we need to remove the key and all its children
 *
 * write a recursive function to remove the key and its children
 *
 * @param key
 * @param content
 */
export const removeComponentByKey = (key: string, content: Content) => {
  let newContent = { ...content };
  if (!newContent[key]) {
    return newContent;
  }
  if (newContent[key].children) {
    newContent[key].children.forEach((child) => {
      newContent = removeComponentByKey(child, newContent);
    });
  }
  delete newContent[key];
  return newContent;
};

/**
 * get all config from content with a given id
 * a component can have one or more children
 * so we need to get the component and all its children
 *
 * write a recursive function to get the component and its children
 *
 * @param id
 * @param content
 */
export const getContentById = (id: string, content: Content = {}): Content => {
  let result = {};

  if (content[id]) {
    result = {
      [id]: content[id],
      ...{
        ...content[id]?.children?.reduce((acc, child) => {
          return {
            ...acc,
            ...getContentById(child, content),
          };
        }, {}),
      },
    };
  }

  return result as Content;
};

const getGradientBackground = (backgroundColors?: string[]) => {
  const colors = backgroundColors?.filter(Boolean) || [];
  if (!colors.length) return undefined;
  if (colors.length === 1) return colors[0];
  return `linear-gradient(to right, ${colors.join(', ')})`;
};

const overrideRootBackground = (style: unknown = {}, background?: string) => {
  const nextStyle: Record<string, unknown> = {
    ...(typeof style === 'object' ? style : {}),
  };

  delete nextStyle.backgroundColor;
  delete nextStyle.backgroundImage;

  if (background) {
    nextStyle.background = background;
  }

  return nextStyle;
};

export const applyPageBackgroundColors = (
  content: Content,
  backgroundColors?: string[],
) => {
  const background = getGradientBackground(backgroundColors);
  const rootNode = content?.['ROOT'];

  if (!content || !rootNode || !background) return content;

  const rootStyle = rootNode.style || {};

  return {
    ...content,
    ['ROOT']: {
      ...rootNode,
      style: {
        ...overrideRootBackground(rootStyle, background),
        desktop: overrideRootBackground(rootStyle.desktop, background),
        mobile: overrideRootBackground(rootStyle.mobile, background),
      },
    },
  };
};
